import { DownloadFileButton } from '@components/DownloadFileButton/index';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  transformHtmlInJSX,
  transformNewlinesInBrTags,
} from '@utils/string-transformations';
import { GTM } from '@app/gtm';
import { jobCompanyNameSelector } from '@model/Job/Job.selectors';
import { getTechnicalReportSelector } from '@model/Candidate/Candidate.selectors';
import { Accordion } from '@reverse-hr/design-system';

export const ScoutReport = ({ scoutReport }) => {
  const { t } = useTranslation(null, {
    keyPrefix: 'candidateDetails.scout',
  });

  const { candidateId, jobId } = useParams();
  const companyName = useStoreState(jobCompanyNameSelector);
  const getTechnicalReport = useStoreActions(getTechnicalReportSelector);

  const trackScoutReportEvent = eventType => {
    GTM.trackGTMEvent({
      action: 'report_scout',
      label: `${jobId} - ${candidateId} - ${eventType}`,
    });

    GTM.trackGA4Event(
      'report_scout',
      companyName,
      `${jobId} - ${candidateId} - ${eventType}`,
    );
  };

  const onDownloadReportClick = async () => {
    try {
      return await getTechnicalReport({ candidateId, jobId });
    } finally {
      trackScoutReportEvent('download');
    }
  };

  const onAccordionToggle = reportType => trackScoutReportEvent(reportType);

  return (
    <div className="rev-o-container">
      <div className="rev-o-grid">
        <div className="rev-o-col-12 rev-u-px-16 rev-u-mt-40">
          <div className="rev-o-antipodes rev-u-py-32">
            <p className="rev-o-antipodes__item rev-o-typo-heading-5 rev-u-color-primary-100">
              {t('title')}
            </p>

            <DownloadFileButton
              className="rev-o-antipodes__item"
              onClick={onDownloadReportClick}
              label={t('button')}
            />
          </div>
        </div>

        {SCOUT_REPORT_TYPE_LIST.map(reportType => (
          <div
            key={reportType}
            className="rev-u-px-16 rev-o-col-12 rev-o-col--medium-6 rev-u-pb-16"
          >
            <Accordion
              onToggle={() => onAccordionToggle(reportType)}
              title={t('accordionTitle', { context: reportType })}
            >
              {transformHtmlInJSX(
                transformNewlinesInBrTags(scoutReport[reportType]),
              )}
            </Accordion>
          </div>
        ))}
      </div>
    </div>
  );
};

ScoutReport.propTypes = {
  scoutReport: PropTypes.shape({
    skillsAssessment: PropTypes.string.isRequired,
    overallAssessment: PropTypes.string.isRequired,
  }),
};

const SCOUT_REPORT_TYPES = {
  SKILLS_ASSESSMENT: 'skillsAssessment',
  OVERALL_ASSESSMENT: 'overallAssessment',
};

const SCOUT_REPORT_TYPE_LIST = Object.values(SCOUT_REPORT_TYPES);
